import { motion } from 'framer-motion'
import { Instagram } from 'lucide-react'
import { cn } from 'lib/utils'
import type { ScanInstagramProfilePayload } from 'modules/creative/api/brand-kit-api'

interface InstagramPolaroidProps {
  profile: ScanInstagramProfilePayload | null | undefined
  className?: string
}

const compactNumber = (value: number | null | undefined): string => {
  if (value == null) return '—'
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value
  )
}

export const InstagramPolaroid: React.FC<InstagramPolaroidProps> = ({ profile, className }) => {
  if (!profile) {
    return (
      <div
        data-testid="instagram-polaroid-skeleton"
        className={cn('card flex h-full w-[220px] flex-col p-2.5', className)}
      >
        <div className="min-h-0 w-[200px] flex-1 animate-pulse rounded-[8px] border border-line bg-bg-3" />
      </div>
    )
  }

  // Always reserve a 9-tile grid even if Business Discovery returned fewer posts —
  // an empty cell preserves the unmistakable IG-grid silhouette so the polaroid
  // never reads as "broken card" when a brand-new account has 1-2 posts.
  const tiles = Array.from({ length: 9 }, (_, i) => profile.posts[i] ?? null)

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.6, 1] }}
      className={cn('card flex h-full w-[220px] flex-col p-2.5', className)}
    >
      <div className="relative min-h-0 w-[200px] flex-1 overflow-hidden rounded-[8px] border border-line bg-bg">
        <div className="flex h-full flex-col p-2.5">
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full border border-line bg-bg-3">
              {profile.profilePictureUrl ? (
                <img
                  src={profile.profilePictureUrl}
                  alt={profile.username}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-paper-dim">
                  <Instagram size={16} />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] leading-tight font-semibold text-paper">
                {profile.username}
              </div>
              {profile.name && (
                <div className="truncate text-[10px] leading-tight text-paper-dim">
                  {profile.name}
                </div>
              )}
            </div>
          </div>

          {profile.biography && (
            <p className="mt-1.5 line-clamp-2 text-[9px] leading-snug text-paper-dim">
              {profile.biography}
            </p>
          )}

          {/* Compact stat row — followers + posts is enough for the polaroid;
              following count just adds noise at this size. */}
          <div className="mt-1.5 flex items-center gap-2 text-[9px] text-paper-faint tabular-nums">
            <span>
              <span className="font-semibold text-paper">
                {compactNumber(profile.followersCount)}
              </span>{' '}
              followers
            </span>
            <span className="text-line">·</span>
            <span>
              <span className="font-semibold text-paper">{compactNumber(profile.mediaCount)}</span>{' '}
              posts
            </span>
          </div>

          {/* 3×3 grid — for VIDEO posts mediaUrl is the .mp4 (which an <img>
              can't render), so always prefer the thumbnail when present. */}
          <div className="mt-2 grid flex-1 grid-cols-3 gap-px overflow-hidden rounded-[4px] border border-line">
            {tiles.map((post, i) => {
              const src = post?.thumbnailUrl || post?.mediaUrl
              return (
                <div key={i} className="relative aspect-square overflow-hidden bg-bg-3">
                  {src && (
                    <img
                      src={src}
                      alt=""
                      className="h-full w-full object-cover"
                      draggable={false}
                      loading="lazy"
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
